import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, CircleHelp, Cookie, CreditCard, Info, Languages, LogOut, MessageCircle, Settings, UserRound } from 'lucide-react';
import { cn, firstName, useOnClickOutside } from '@/lib/utils';
import { Avatar, Portal, type AvatarSize } from '@/components/ui';
import { useMe } from '@/lib/api';
import { useAuth, roleLabel } from '@/lib/auth';
import { planLabel } from '@/lib/billing';
import { openCookieSettings } from '@/lib/consent';
import { UserMenuSubsection, type UserMenuSection } from './UserMenuSubsection';
import './UserMenu.css';

export interface UserMenuProps {
  /** Avatar-only chip when the sidebar is collapsed to an icon rail. */
  collapsed?: boolean;
  /**
   * O avatar: 24px na barra lateral (o padrão); no celular, 32px na barra do
   * topo e 40px no rodapé da gaveta, os alvos de toque maiores de lá.
   */
  avatarSize?: AvatarSize;
  /**
   * O plano da conta numa linha sob o primeiro nome ("Gratuito", "Pro"), como o
   * app do Gemini: só no rodapé da gaveta do celular. O chip do desktop fica só
   * com o nome.
   */
  showPlan?: boolean;
  /**
   * Chama a Shiba de volta (o widget flutuante): é o caminho de quem ocultou a
   * pílula. Ausente onde o widget não é montado, e aí o item nem aparece.
   */
  onAskShiba?: () => void;
}

/** Folga entre o chip e o painel, e entre o painel e a borda da janela. */
const GAP = 8;

/**
 * Acima do chip, alinhado à esquerda dele, sempre inteiro na janela: abaixo do
 * chip quando não cabe em cima, e encostado na borda quando passaria dela.
 */
export function placeAboveTrigger(
  trigger: DOMRect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
): { top: number; left: number } {
  const above = trigger.top - GAP - panel.height;
  const top = above >= GAP ? above : Math.min(trigger.bottom + GAP, viewport.height - panel.height - GAP);
  const left = Math.min(trigger.left, viewport.width - panel.width - GAP);
  return { top: Math.max(GAP, top), left: Math.max(GAP, left) };
}

/**
 * Identity chip in the sidebar footer: avatar + the person's first name (only the
 * avatar on the rail; in the phone drawer, the plan under the name), opening a
 * menu (upward) whose header carries name, e-mail and role, then the theme
 * choice, "Perguntar à Shiba" (where the widget lives), settings, password
 * change, cookie preferences and sign-out. Closes on outside click and Escape;
 * the trigger reflects expanded state for assistive tech. O e-mail saiu do
 * chip, mas fica no nome acessível dele e no cabeçalho do menu.
 * No celular ele é também o avatar da barra do topo (só o avatar, como no rail),
 * e ali o painel abre para baixo, porque em cima do chip não cabe.
 *
 * O painel mora num Portal com `position: fixed`, medido a partir do chip: dentro
 * da barra ele seria cortado — ela rola por dentro (`overflow`) e, como gaveta,
 * tem `transform`, que prende até um `fixed` descendente. Abrir leva o foco ao
 * primeiro item (fora da ordem do Tab do rail, ele seria inalcançável), e `Tab`
 * fecha o menu devolvendo o foco ao chip, de onde a tabulação segue.
 *
 * O papel saiu do chip: não é estado, e uma pílula a mais no rodapé do rail era
 * ruído permanente para um dado que só interessa ao abrir o menu.
 */
export function UserMenu({
  collapsed = false,
  avatarSize = 'sm',
  showPlan = false,
  onAskShiba,
}: UserMenuProps): JSX.Element {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<UserMenuSection | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Estado, e não só ref: o Portal monta o painel um commit depois da abertura,
  // e é com ele no DOM que dá para medir.
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  menuRef.current = panel;
  const hoverTimer = useRef<number | undefined>(undefined);

  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useOnClickOutside([triggerRef, menuRef], () => setOpen(false), open);
  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  // Posiciona antes de pintar e acompanha resize/rolagem (captura, para pegar o
  // rail que rola por dentro). Aberto, o foco vai para o primeiro item.
  useLayoutEffect(() => {
    if (!open || !panel) return;
    const place = (): void => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const viewport = { width: document.documentElement.clientWidth, height: window.innerHeight };
      setCoords(placeAboveTrigger(trigger.getBoundingClientRect(), panel.getBoundingClientRect(), viewport));
    };
    place();
    panel.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, panel, section]);

  if (!user) return <></>;

  const name = firstName(user.displayName, user.email);
  const close = (): void => {
    window.clearTimeout(hoverTimer.current);
    setOpen(false);
    setSection(null);
  };
  const scheduleSection = (next: UserMenuSection): void => {
    window.clearTimeout(hoverTimer.current);
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    hoverTimer.current = window.setTimeout(() => setSection(next), 200);
  };
  const navigateTo = (destination: string): void => {
    close();
    if (destination.startsWith('mailto:')) window.location.href = destination;
    else navigate(destination);
  };

  /**
   * Esc fecha o menu e para aqui: o menu mora dentro da gaveta da navegação (em
   * telas estreitas), que também fecha no Esc — uma tecla, uma superfície. Vale
   * no gatilho e nos itens (o Portal propaga pela árvore do React); o foco volta
   * ao gatilho, que continua na tela. `Tab` num item também fecha e devolve o
   * foco ao gatilho, sem impedir a tecla: a tabulação segue a partir dele.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!open || (event.key !== 'Escape' && event.key !== 'Tab')) return;
    if (event.key === 'Tab' && !menuRef.current?.contains(event.target as Node)) return;
    if (event.key === 'Escape') event.stopPropagation();
    close();
    triggerRef.current?.focus();
  };

  return (
    <>
      <div className={cn('user-menu', collapsed && 'user-menu--collapsed')} onKeyDown={onKeyDown}>
        <button
          ref={triggerRef}
          type="button"
          className="user-menu__trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          // Começa pelo nome visível (WCAG 2.5.3) e leva o e-mail: diz de qual
          // conta é o menu, também no rail, onde só sobra o avatar.
          aria-label={`${name} (${user.email})`}
          onClick={() => {
            if (open) close();
            else {
              setSection(null);
              setOpen(true);
            }
          }}
        >
          <UserAvatar name={user.displayName} size={avatarSize} />
          {!collapsed && (
            <span className="user-menu__identity">
              <span className="user-menu__name">{name}</span>
              {showPlan && <AccountPlan />}
            </span>
          )}
        </button>

        {open && (
          <Portal>
            <div
              ref={setPanel}
              className="user-menu__panel"
              role="menu"
              aria-label="Conta"
              // Um quadro invisível antes de medir: sem isto o painel pisca no canto da tela.
              style={{ top: coords?.top ?? 0, left: coords?.left ?? 0, visibility: coords ? undefined : 'hidden' }}
            >
              {section ? (
                <UserMenuSubsection section={section} onBack={() => setSection(null)} onNavigate={navigateTo} />
              ) : (
                <>
              <div className="user-menu__header">
                <p className="user-menu__header-name">{user.displayName}</p>
                <p className="user-menu__header-email">{user.email}</p>
                <p className="user-menu__header-role">{roleLabel(user.role)}</p>
              </div>
              <div className="user-menu__divider" role="separator" />
              {onAskShiba && (
                <button
                  type="button"
                  role="menuitem"
                  className="user-menu__item"
                  onClick={() => {
                    close();
                    onAskShiba();
                  }}
                >
                  <span className="user-menu__item-icon" aria-hidden="true">
                    <MessageCircle size={16} />
                  </span>
                  Perguntar à Shiba
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="user-menu__item"
                onClick={() => {
                  close();
                  navigate('/app/conta/seguranca');
                }}
              >
                <span className="user-menu__item-icon" aria-hidden="true">
                  <UserRound size={16} />
                </span>
                Minha conta
              </button>
              <button type="button" role="menuitem" className="user-menu__item" onClick={() => navigateTo('/app/conta/assinatura')}>
                <span className="user-menu__item-icon" aria-hidden="true"><CreditCard size={16} /></span>
                Assinatura
              </button>
              {([
                { id: 'about', label: 'Sobre nós', icon: Info },
                { id: 'language', label: 'Idioma', icon: Languages },
                { id: 'help', label: 'Ajuda', icon: CircleHelp },
              ] as const).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className="user-menu__item"
                  aria-haspopup="menu"
                  onClick={() => {
                    window.clearTimeout(hoverTimer.current);
                    setSection(item.id);
                  }}
                  onMouseEnter={() => scheduleSection(item.id)}
                  onMouseLeave={() => window.clearTimeout(hoverTimer.current)}
                >
                  <span className="user-menu__item-icon" aria-hidden="true"><item.icon size={16} /></span>
                  {item.label}
                  <span className="user-menu__item-check" aria-hidden="true"><ChevronRight size={15} /></span>
                </button>
              ))}
              <button
                type="button"
                role="menuitem"
                className="user-menu__item"
                onClick={() => {
                  close();
                  navigate('/app/configuracoes');
                }}
              >
                <span className="user-menu__item-icon" aria-hidden="true">
                  <Settings size={16} />
                </span>
                Configurações
              </button>
              <button
                type="button"
                role="menuitem"
                className="user-menu__item"
                onClick={() => {
                  close();
                  openCookieSettings();
                }}
              >
                <span className="user-menu__item-icon" aria-hidden="true">
                  <Cookie size={16} />
                </span>
                Preferências de cookies
              </button>
              <div className="user-menu__divider" role="separator" />
              <button
                type="button"
                role="menuitem"
                className={cn('user-menu__item', 'user-menu__item--danger')}
                onClick={() => {
                  close();
                  void signOut();
                }}
              >
                <span className="user-menu__item-icon" aria-hidden="true">
                  <LogOut size={16} />
                </span>
                Encerrar sessão
              </button>
                </>
              )}
            </div>
          </Portal>
        )}
      </div>
    </>
  );
}

/** Só monta com sessão: sem usuário, a leitura do /me nem é criada. */
function UserAvatar({ name, size }: { name: string; size: AvatarSize }): JSX.Element {
  const me = useMe();
  return <Avatar name={name} src={me.data?.avatarUrl} size={size} className="user-menu__avatar" />;
}

/**
 * A linha do plano sob o nome. Lê o /me por conta própria, como a cota do rodapé
 * (o TanStack junta as leituras), e só monta onde é pedida: o chip do desktop e
 * a barra do topo nem consultam. Sem plano conhecido, não há linha.
 */
function AccountPlan(): JSX.Element | null {
  const label = planLabel(useMe().data?.plan);
  return label ? <span className="user-menu__plan">{label}</span> : null;
}
